import { ArrayUtil, DisplayType, HorizontalAlignment, ModelRow, ValueOrFunction, ValueResolver } from "@mcleod/core";
import {
    Component, ComponentCreator, ComponentFactory, Layout, LayoutProps, Panel, Textbox, TextboxProps
} from "@mcleod/components";
import { KeyValuePair } from "./KeyValuePair";
import { PanelKeyValuePairs } from "./PanelKeyValuePairs";

export class CommonTooltips {
    /**
     * This method creates a panel of key/value pairs, for the purpose of showing the values in a tooltip.
     *
     * @param heading The content to display above the key value pairs
     * @param keyValuePairs
     * @returns The Panel that contains the resulting tooltip content.
     */
    public static createKeyValueTooltipContent(keyValuePairs: KeyValuePair[], header?: ComponentCreator,
        footer?: ComponentCreator, indentPairs?: boolean): Panel {
        const panel = new Panel({ fillRow: true, margin: 0, padding: 0 });
        if (header != null) {
            const component = ComponentFactory.createCommon(header);
            panel.add(component);
        }
        const keyValuePanel = new PanelKeyValuePairs({ margin: 0, padding: 0 }, keyValuePairs);
        if (indentPairs) {
            keyValuePanel.marginLeft = 30;
        }
        panel.add(keyValuePanel);
        if (footer != null) {
            const component = ComponentFactory.createCommon(footer);
            panel.add(component);
        }
        return panel;
    }

    /**
     * Sets a component's dynamic tooltip content equal to a function that loads the specified layout, including
     * populating its data.
     *
     * @param component The component to which the tooltip should be applied
     * @param idOrData Either the id of the record to load in the layout (a string value), or a ModelRow to display in
     *                 the layout, or an array of ModelRows to display in the layout
     * @param layoutName The name of the layout to load
     * @param layoutProps Any LayoutProps to assign to the layout
     * @param searchFilter A json object containing the values to use when searching the layout's main DataSource.  This
     *                     is used when the idOrData parameter does not contain a ModelRow or an array of ModelRows;
     *                     when that happens, the searchFilter will be used to search the layout's main DataSource.
     */
    public static setTooltipContentLayout(component: Component, idOrData: string | ModelRow<any> | ModelRow<any>[],
        layoutName: string, layoutProps?: Partial<LayoutProps>, searchFilter?: any) {
        if (component == null || (idOrData == null && searchFilter == null)) {
            return;
        }
        component.tooltipDynamicContent = async () => {
            return Layout.getLoadedLayoutWithData(layoutName, layoutProps, idOrData, searchFilter);
        }
    }

    /**
     * Sets a component's dynamic tooltip content equal to a function that builds a panel of printable textboxes
     * (based on the supplied textbox properties).
     *
     * @param component The component to which the tooltip should be applied
     * @param textboxProps The properties to use when building the textboxes.  This can be supplied as an array of
     *                     Partial<TextboxProps>, or as a functin that returns an array of Partial<TextboxProps>.
     * @param isBold Indicates if the texboxes' text should be bold
     */
    public static setTooltipContentTextboxes(component: Component,
        textboxProps: ValueOrFunction<Partial<TextboxProps>[]>, isBold: boolean = true) {
        if (component == null || textboxProps == null) {
            return;
        }
        if (typeof textboxProps === "function") {
            component.tooltipDynamicContent = async () => {
                const resolvedTextboxProps = ValueResolver.resolve(textboxProps);
                return this.buildPanelOfTextboxes(resolvedTextboxProps, isBold);
            };
        } else {
            component.tooltipDynamicContent = this.buildPanelOfTextboxes(textboxProps, isBold);
        }
    }

    private static buildPanelOfTextboxes(textboxProps: Partial<TextboxProps>[], isBold: boolean) {
        if (ArrayUtil.isEmptyArray(textboxProps)) {
            return null;
        }
        const panel = Panel.createNoMarginNoPaddingPanel();
        textboxProps.forEach(props => {
            const textbox = new Textbox({ ...props, printable: true, fontBold: isBold });
            if (textbox.displayType === DisplayType.CURRENCY) {
                textbox.printableLabel.align = HorizontalAlignment.LEFT;
            }
            panel.add(textbox);
        });
        return panel;
    }
}
